import React from "react"
import moment from "moment"
import Box from "@mui/material/Box"
import { FaDownload } from "react-icons/fa"
import { Button } from "primereact/button"
import DOMPurify from "dompurify"

import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarQuickFilter,
  GridRenderCellParams,
} from "@mui/x-data-grid"

const DownloadIcon = FaDownload as React.FC<{ className?: string }>

const CustomToolbar = ({ downloadResults, downLoadLoading }: any) => {
  return (
    <GridToolbarContainer sx={{ justifyContent: "space-between" }}>
      {/*<GridToolbarExport  printOptions={{ disableToolbarButton: true }} />*/}
      <GridToolbarQuickFilter />
      <ExportData
        downloadResults={downloadResults}
        downLoadLoading={downLoadLoading}
      />
    </GridToolbarContainer>
  )
}

const ExportData = ({ downloadResults, downLoadLoading }: any) => {
  return (
    <Box display="flex" justifyContent="flex-end">
      <Button
        onClick={downloadResults}
        className="btn btn-light text-primary border mb-2 mt-1"
        disabled={downLoadLoading}
      >
        {downLoadLoading ? (
          "Loading…"
        ) : (
          <>
            <DownloadIcon /> Export
          </>
        )}
      </Button>
    </Box>
  )
}

const ResultsTable = ({
  rowCount,
  rows,
  pageData,
  handleServerSearch,
  downloadResults,
  downLoadLoading,
  qString,
  showOfficersDataColumn
}: any) => {
  const handlePageChange = (params: any) => {
    handleServerSearch(params)
  }

  const formatDate = (str: string) => {
    if (!str || str.length < 8) {
      return str;
    }
    try {
      return moment(moment(str, 'YYYYMMDD')).format("MM/DD/yyyy");
    } catch (e) {
      console.error(e);
    }
    return str;
  }

  const row = rows.map((i: any) => ({
    ...i,
    entityNameHighlights:
      i.highlights && i.highlights.entityName
        ? i.highlights.entityName.join(' ')
        : i.entityName,
    entityNameRecord: i.entityName,
    currencydateFormat: formatDate(i.currencydate),
    filingDateFormat: formatDate(i.filingDate),
    annualRptFilingDtFormat: formatDate(i.annualRptFilingDt),
    officers: i.beOfficers && i.beOfficers.length > 0 ? 'Yes' : 'No'
  }))
  const columns = [
    {
      field: "entityNameHighlights",
      headerName: "Entity Name",
      width: 200,
      headerClassName: "super-app-theme--header",
      renderCell: (params: GridRenderCellParams<any>) => (
        <div
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(params.formattedValue, {
              ALLOWED_TAGS: ["highlight"],
            }),
          }}
        />
      ),
    },
    {
      field: "entityNameRecord",
      headerName: "Entity Name as listed on the state record",
      width: 200,
      headerClassName: "super-app-theme--header",
    },
    {
      field: "typedesc",
      headerName: "Entity Type",
      width: 180,
      headerClassName: "super-app-theme--header",
    },
    {
      field: "domesticJurisdiction",
      headerName: "Domestic Jurisdiction",
      width: 150,
      headerClassName: "super-app-theme--header",
    },
    {
      field: "filingStateCd",
      headerName: "Jurisdiction Audited",
      width: 150,
      headerClassName: "super-app-theme--header",
    },
    {
      field: "stateId",
      headerName: "State ID#",
      width: 150,
      headerClassName: "super-app-theme--header",
    },
    {
      field: "filingDateFormat",
      headerName: "Filing Date",
      width: 100,
      headerClassName: "super-app-theme--header",
    },
    {
      field: "agentName",
      headerName: "Agent Name",
      width: 200,
      headerClassName: "super-app-theme--header",
    },
    {
      field: "agentAddress",
      headerName: "Agent Address",
      width: 200,
      headerClassName: "super-app-theme--header",
    },
    {
      field: "statdesc",
      headerName: "SOS Status",
      width: 150,
      headerClassName: "super-app-theme--header",
    },
    {
      field: "activeFlg",
      headerName: "Active on SOS",
      width: 150,
      headerClassName: "super-app-theme--header",
    },
    {
      field: "inArrow",
      headerName: "In Arrow?",
      width: 150,
      headerClassName: "super-app-theme--header",
    },
    {
      field: "statusInArrow",
      headerName: "Status in Arrow",
      width: 150,
      headerClassName: "super-app-theme--header",
    },
    {
      field: "entityAddress",
      headerName: "Entity Address",
      width: 200,
      headerClassName: "super-app-theme--header",
    },
    {
      field: "currencydateFormat",
      headerName: "Currency Date",
      width: 120,
      headerClassName: "super-app-theme--header",
    },
    {
      field: "annualRptFilingDtFormat",
      headerName: "Last Annual Report Filed",
      width: 170,
      headerClassName: "super-app-theme--header",
    },
    ...(showOfficersDataColumn
      ? [
          {
            field: "officers",
            headerName: "Officer Data",
            width: 170,
            headerClassName: "super-app-theme--header",
          },
        ]
      : []),
  ]

  return (
    <>
      {!row || row.length === 0 ? (
        <p className="text-center wk-size-5 mt-8">
          <strong>({rowCount})</strong> search result found.
          {/* for{" "} <strong>{qString}</strong> */}
        </p>
      ) : (
        <>
          <h6 className="mt-2">Search Results ({rowCount})</h6>
          <Box
            sx={{
              height: "calc(100vh - 280px)",
              width: "100%",
              "& .super-app-theme--header": {
                backgroundColor: "rgba(200, 200, 200, 0.55)",
              },
            }}
          >
            <DataGrid
              getRowId={row => row.entityId}
              getRowHeight={() => "auto"}
              rows={row}
              columns={columns}
              pagination
              pageSizeOptions={[10, 25, 50, 100]}
              paginationMode="server"
              paginationModel={pageData}
              onPaginationModelChange={handlePageChange}
              rowCount={rowCount}
              disableColumnMenu={true}
              slots={{
                toolbar: () =>
                  CustomToolbar({ downloadResults, downLoadLoading })
              }}
              sx={{
                fontSize: 12,
                "& .MuiDataGrid-cell": {
                  padding: "10px",
                  wordWrap: "break-word",
                  wordBreak: "break-all",
                },
                ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows":
                {
                  marginTop: "1em",
                  marginBottom: "1em",
                },
                ".MuiDataGrid-columnHeaderTitle": {
                  fontWeight: 700,
                },
              }}
            />
          </Box>
        </>
      )}
    </>
  )
}

export default ResultsTable
